/* instanbul ignore file */
import React, {Suspense, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {InlineNotification, ItemsType} from '@veneer/core';

import {GlobalTranslationsContext, Loading, useTenantContext} from '@br/idm-components';
import {ActionsProps, type AppHeaderProps, BasicPage, NavProps, generatePath} from '@br/mg-components';

import {useSettings} from '../../../contexts/SettingsContext';
import {useNullableUserContext} from '../../../contexts/UserContext';
import {ErrorBoundaryWrapper} from '../../ErrorBoundary';

import {getLanguages, setLanguage, useSideMenuCallback, useUserActions} from './utils';

const BasePage = ({
    nav,
    children,
    headerActions,
    breadcrumbs = [],
    padContent = true,
}: {
    nav?: React.JSXElementConstructor<NavProps>;
    breadcrumbs?: ItemsType[];
    headerActions?: React.ReactNode;
    children: React.ReactNode;
    padContent?: boolean;
}): JSX.Element => {
    const sideMenuToggleCallback = useSideMenuCallback();
    const user = useNullableUserContext();

    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);
    const {t, i18n} = useTranslation(['BasePage']);

    const navigate = useNavigate();

    const {tenant} = useTenantContext();

    const extraInfoRequired = useMemo(() => {
        return (
            tenant &&
            user &&
            !user.isSupportUser &&
            (tenant.isBusiness === undefined || tenant.isBusiness === null || tenant.contactEmail === '')
        );
    }, [tenant, user]);

    const settings = useSettings();

    const userActions = useUserActions();

    const actions: ActionsProps = {
        setLanguage: setLanguage,
        getLanguages: getLanguages,
        currentLanguage: i18n.language,
        contextHelpUrl: settings.HPWOLF_SUPPORT_URL,
        showNotificationCenter: user !== null,
        userActions: userActions,
    };

    let loginType: AppHeaderProps['loginType'] = 'default';
    if (user?.isHpIdUser) {
        loginType = 'HPID';
    } else if (user?.isAzureADUser) {
        loginType = 'AAD';
    }

    return (
        <BasicPage
            loginType={loginType}
            actions={actions}
            sideMenuOnToggle={sideMenuToggleCallback}
            sideMenuDefaultPinned={
                user?.preferences?.is_nav_pinned === undefined ? true : user?.preferences?.is_nav_pinned
            }
            nav={nav}
            breadcrumbs={breadcrumbs}
            userInfo={
                user && {
                    username: user.username,
                    email: user.email,
                    manageAccountUrl: user.isHpIdUser ? user.hpIdAccountUrl : undefined,
                    logoutLink: '/logout/', // eslint-disable-line i18next/no-literal-string
                }
            }
            environment={settings.WEX_ENVIRONMENT}
            disableAppSwitcher={!settings.APP_SWITCHER_ENABLED}
            headerActions={headerActions}
            padContent={padContent}
        >
            {extraInfoRequired && (
                <InlineNotification
                    action={{
                        action: () =>
                            navigate(generatePath('/portal/tenant/:tenantId/settings/', {tenantId: tenant.id})),
                        label: t('BasePage:inlineExtraInfoLinkToSettings', 'Add now'),
                    }}
                    closeButton={false}
                    title={t('BasePage:inlineExtraInfoTitle', 'Extra information required')}
                    description={t(
                        'BasePage:inlineExtraInfoDescription',
                        'Some important information is required to complete your account registration.'
                    )}
                    type="warning"
                    i18n={veneerTranslations.inlineNotification}
                />
            )}
            <Suspense fallback={<Loading />}>
                <ErrorBoundaryWrapper>{children}</ErrorBoundaryWrapper>
            </Suspense>
        </BasicPage>
    );
};

export default BasePage;
